import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import Button from '@/components-latest/primitives/Button'
import ContentBlock from '@/components-latest/primitives/ContentBlock'
import { Plus } from '@/components-latest/primitives/Icons'
import Spinner from '@/components-latest/primitives/Spinner'
import Text from '@/components-latest/primitives/Text'
import MenuItemCard from '@/components-latest/ui/OfferPage/MenuItemCard'
import SelectMenuItemsModal from '@/components-latest/ui/OfferPage/OfferDetailsForm/SelectMenuItemsModal'
import { SupportedLanguages } from '@/config/i18n'
import { useLazyFetchMenuListQuery } from '@/ducks/menu/api/menu.api'
import { selectStoreId } from '@/ducks/store/selectors/store.selectors'
import { useToggle } from '@/hooks/useToggle/useToggle'

import Styles from './SelectMenuItems.module.scss'

type Props = {
  chosenMenuItemsIds: string[]
  setChosenMenuItemsIds: React.Dispatch<React.SetStateAction<string[]>>
}

const SelectMenuItems = ({
  chosenMenuItemsIds,
  setChosenMenuItemsIds
}: Props) => {
  const { t, i18n } = useTranslation()

  const language = (i18n.resolvedLanguage || 'en') as SupportedLanguages

  const storeId = useSelector(selectStoreId)

  const [requestMenu, { data, isSuccess, isFetching }] =
    useLazyFetchMenuListQuery()

  useEffect(() => {
    if (storeId) {
      requestMenu({ store_id: storeId })
    }
  }, [requestMenu, storeId])

  const [isSelectItemsModalOpen, toggleSelectItemsModalOpen] = useToggle(false)

  const categoriesToShow = useMemo(() => {
    return (
      data?.menu?.mainCategories?.reduce(
        (acc, category) => {
          if (
            category.items.some(item => chosenMenuItemsIds.includes(item._id))
          ) {
            acc.mainCategories.add(category._id)
          }

          category.subCategories.forEach(subcategory => {
            if (
              subcategory.items.some(item =>
                chosenMenuItemsIds.includes(item._id)
              )
            ) {
              acc.mainCategories.add(category._id)
              acc.subCategories.add(subcategory._id)
            }
          })

          return acc
        },
        {
          mainCategories: new Set<string>(),
          subCategories: new Set<string>()
        }
      ) || {
        mainCategories: new Set<string>(),
        subCategories: new Set<string>()
      }
    )
  }, [chosenMenuItemsIds, data])

  const isDataLoaded = !isFetching && isSuccess && data

  return (
    <>
      <SelectMenuItemsModal
        onSubmit={itemsIds => {
          setChosenMenuItemsIds(itemsIds)
        }}
        initialMenuItemsIds={chosenMenuItemsIds}
        disableYassirManagedItems
        isOpen={isSelectItemsModalOpen}
        onCloseModal={toggleSelectItemsModalOpen}
      />
      <ContentBlock className={Styles.contentBlock}>
        <Text variant="title-2">{t('offers.offers-selection.title')}</Text>
        {isFetching && (
          <div className={Styles.spinner}>
            <Spinner />
          </div>
        )}
        {isDataLoaded &&
          (chosenMenuItemsIds.length === 0 ? (
            <div className={Styles.empty}>
              <Text variant="heading-04" align="center">
                {t('offers.offers-selection.empty-title')}
              </Text>
              <Text variant="subtitle" align="center">
                {t('offers.offers-selection.empty-subtitle')}
              </Text>
              <Button
                onClick={toggleSelectItemsModalOpen}
                shape="rounded"
                disabled={isFetching}
                variant="branded"
                icon={<Plus />}>
                {t('offers.offers-selection.select-button')}
              </Button>
            </div>
          ) : (
            <>
              {data.menu.mainCategories.map(category => {
                if (!categoriesToShow.mainCategories.has(category._id)) {
                  return null
                }

                const shouldShowMainCategoryItem = category.items.some(item =>
                  chosenMenuItemsIds.includes(item._id)
                )

                return (
                  <React.Fragment key={category._id}>
                    {shouldShowMainCategoryItem && (
                      <Text className={Styles.title} variant="nextSubtitle">
                        {category.name[language]}
                      </Text>
                    )}
                    <div className={Styles.items}>
                      {category.items.map(item => {
                        if (!chosenMenuItemsIds.includes(item._id)) return null
                        return (
                          <MenuItemCard
                            name={item.name}
                            _id={item._id}
                            price={item.price}
                            avatar={item.avatar}
                            key={item._id}
                          />
                        )
                      })}
                    </div>

                    {category.subCategories.map(subcategory => {
                      if (
                        !categoriesToShow.subCategories.has(subcategory._id)
                      ) {
                        return null
                      }

                      const categoryName = category.name[language]
                      const subcategoryName = subcategory.name[language]

                      return (
                        <React.Fragment key={subcategory._id}>
                          <Text className={Styles.title} variant="nextSubtitle">
                            {categoryName} / {subcategoryName}
                          </Text>
                          <div className={Styles.items}>
                            {subcategory.items.map(item => {
                              if (!chosenMenuItemsIds.includes(item._id)) {
                                return null
                              }

                              return (
                                <MenuItemCard
                                  name={item.name}
                                  _id={item._id}
                                  price={item.price}
                                  avatar={item.avatar}
                                  key={item._id}
                                />
                              )
                            })}
                          </div>
                        </React.Fragment>
                      )
                    })}
                  </React.Fragment>
                )
              })}
              <Button
                onClick={toggleSelectItemsModalOpen}
                variant="primary"
                className={Styles.edit}>
                {t('offers.offers-selection.edit-button')}
              </Button>
            </>
          ))}
      </ContentBlock>
    </>
  )
}

export default SelectMenuItems
